import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Upload, X, FileText, Download, AlertTriangle, Trophy, TrendingUp, History } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Production URL for downloads - always use cloudrfq.in domain
const PRODUCTION_API = 'https://www.cloudrfq.in/api';

const QuoteSubmission = () => {
  const { token } = useParams();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);  // Prevent double submission
  const [customFields, setCustomFields] = useState([]);
  const [rfqFieldResponses, setRfqFieldResponses] = useState([]);
  const [itemQuotes, setItemQuotes] = useState([]);  // Full quote data for each additional item
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [rankingData, setRankingData] = useState(null);  // Supplier's current ranking
  const [formData, setFormData] = useState({
    rate: '',
    moq: '',
    delivery_time: '',
    terms: '',
    item_not_available: false,
  });

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/quotes/verify/${token}`);
      setVerificationData(response.data);

      // Initialize RFQ custom field responses for primary item (Item 1)
      if (response.data.rfq?.custom_fields) {
        const initialResponses = response.data.rfq.custom_fields
          .filter(f => f.type !== 'file')
          .map(field => ({
            field_id: field.id || field.label,
            label: field.label,
            type: field.type,
            options: field.options || [],
            value: '',
          }));
        setRfqFieldResponses(initialResponses);
      }

      // Initialize item quotes for additional items (Item 2, 3, etc.)
      if (response.data.rfq?.items && response.data.rfq.items.length > 0) {
        const initialItemQuotes = response.data.rfq.items.map(item => ({
          item_id: item.id,
          item_name: item.item_name,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          rate: '',
          moq: '',
          delivery_time: '',
          item_not_available: false,
          attachments: [], // Item-wise supplier attachments
          // Initialize custom field responses for this item
          custom_field_responses: (item.custom_fields || [])
            .filter(f => f.type !== 'file')
            .map(field => ({
              field_id: field.id || field.label,
              label: field.label,
              type: field.type,
              options: field.options || [],
              value: '',
            })),
        }));
        setItemQuotes(initialItemQuotes);
      }

      if (response.data.has_submitted && response.data.existing_quote) {
        const quote = response.data.existing_quote;
        setFormData({
          rate: quote.rate.toString(),
          moq: quote.moq.toString(),
          delivery_time: quote.delivery_time,
          terms: quote.terms || '',
          item_not_available: quote.item_not_available || false,
        });
        // Load existing attachments
        if (quote.attachments && quote.attachments.length > 0) {
          setAttachments(quote.attachments);
        }
        // Load existing RFQ field responses
        if (quote.rfq_custom_field_responses && quote.rfq_custom_field_responses.length > 0) {
          setRfqFieldResponses(prev => prev.map(field => {
            const existing = quote.rfq_custom_field_responses.find(r => r.field_id === field.field_id);
            return existing ? { ...field, value: existing.value } : field;
          }));
        }
        // Load existing item quotes
        if (quote.item_quotes && quote.item_quotes.length > 0) {
          setItemQuotes(prev => prev.map(item => {
            const existing = quote.item_quotes.find(q => q.item_id === item.item_id);
            if (existing) {
              return {
                ...item,
                rate: existing.rate?.toString() || '',
                moq: existing.moq?.toString() || '',
                delivery_time: existing.delivery_time || '',
                item_not_available: existing.item_not_available || false,
                custom_field_responses: item.custom_field_responses.map(field => {
                  const existingResponse = (existing.custom_field_responses || []).find(r => r.field_id === field.field_id);
                  return existingResponse ? { ...field, value: existingResponse.value } : field;
                })
              };
            }
            return item;
          }));
        }
        
        // Fetch current ranking data
        fetchRankingData();
      }

      setLoading(false);
    } catch (error) {
      console.error('Error verifying token:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired link');
      setLoading(false);
    }
  };

  const fetchRankingData = async () => {
    try {
      const response = await axios.get(`${API}/quotes/ranking/${token}`);
      setRankingData(response.data);
    } catch (error) {
      console.log('Could not fetch ranking data:', error);
    }
  };

  const updateRfqFieldResponse = (fieldId, value) => {
    setRfqFieldResponses(prev => prev.map(field =>
      field.field_id === fieldId ? { ...field, value } : field
    ));
  };

  const updateItemQuote = (itemId, updates) => {
    setItemQuotes(prev => prev.map(item =>
      item.item_id === itemId ? { ...item, ...updates } : item
    ));
  };

  const updateItemCustomFieldResponse = (itemId, fieldId, value) => {
    setItemQuotes(prev => prev.map(item => {
      if (item.item_id === itemId) {
        return {
          ...item,
          custom_field_responses: item.custom_field_responses.map(field =>
            field.field_id === fieldId ? { ...field, value } : field
          )
        };
      }
      return item;
    }));
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/quotes/upload-attachment/${token}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newAttachments.push(response.data.attachment);
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setAttachments([...attachments, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(a => a.id !== attachmentId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (submitting) {
      toast.info('Please wait, your quotation is being submitted...');
      return;
    }

    // Validate Item 1 - either item_not_available OR valid rate > 0
    if (!formData.item_not_available) {
      // Validate mandatory RFQ field responses for Item 1
      const missingFields = rfqFieldResponses.filter(f => !f.value || f.value.trim() === '');
      if (missingFields.length > 0) {
        toast.error(`Item 1: Please fill in all mandatory fields: ${missingFields.map(f => f.label).join(', ')}`);
        return;
      }
      
      // Validate rate is provided and > 0
      const rate = parseFloat(formData.rate);
      if (!formData.rate || isNaN(rate) || rate <= 0) {
        toast.error('Item 1: Rate must be greater than zero. If you don\'t have this item, please mark it as "Item Not Available".');
        return;
      }
    }

    // Validate item quotes for additional items
    for (let i = 0; i < itemQuotes.length; i++) {
      const item = itemQuotes[i];
      
      // If item is not marked as not available, validate rate and fields
      if (!item.item_not_available) {
        const itemRate = parseFloat(item.rate);
        if (!item.rate || isNaN(itemRate) || itemRate <= 0) {
          toast.error(`Item ${i + 2} (${item.item_name}): Rate must be greater than zero. If you don't have this item, please mark it as "Item Not Available".`);
          return;
        }
        
        // Validate custom field responses for this item
        const missingItemFields = item.custom_field_responses.filter(f => !f.value || f.value.trim() === '');
        if (missingItemFields.length > 0) {
          toast.error(`Item ${i + 2} (${item.item_name}): Please fill in all mandatory fields: ${missingItemFields.map(f => f.label).join(', ')}`);
          return;
        }
      }
    }

    setSubmitting(true);  // Start submission - disable button

    try {
      // First submit the quote
      const submitResponse = await axios.post(`${API}/quotes/submit/${token}`, {
        rate: formData.item_not_available ? 0 : parseFloat(formData.rate),
        moq: formData.item_not_available ? 0 : parseFloat(formData.moq),
        delivery_time: formData.item_not_available ? '' : formData.delivery_time,
        terms: formData.terms,
        item_not_available: formData.item_not_available,
        rfq_custom_field_responses: formData.item_not_available ? [] : rfqFieldResponses.map(f => ({
          field_id: f.field_id,
          label: f.label,
          value: f.value,
        })),
        item_quotes: itemQuotes.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          rate: item.item_not_available ? 0 : parseFloat(item.rate),
          moq: item.item_not_available ? null : (item.moq ? parseFloat(item.moq) : null),
          delivery_time: item.item_not_available ? '' : (item.delivery_time || ''),
          item_not_available: item.item_not_available || false,
          custom_field_responses: item.item_not_available ? [] : item.custom_field_responses.map(f => ({
            field_id: f.field_id,
            label: f.label,
            value: f.value,
          })),
        })),
        supplier_custom_fields: customFields.filter(f => f.label && f.value),
        // Don't send attachments here - they will be uploaded separately
      });
      
      const quoteId = submitResponse.data.quote_id;
      
      // Upload Item 1 attachments (general attachments)
      for (const att of attachments) {
        if (att.file) {
          const fileData = new FormData();
          fileData.append('file', att.file);
          await axios.post(`${API}/quotes/${quoteId}/upload-attachment`, fileData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      
      // Upload item-wise attachments (Item 2, 3, etc.)
      for (const item of itemQuotes) {
        if (item.attachments && item.attachments.length > 0) {
          for (const att of item.attachments) {
            if (att.file) {
              const fileData = new FormData();
              fileData.append('file', att.file);
              fileData.append('item_id', item.item_id);
              await axios.post(`${API}/quotes/${quoteId}/upload-item-attachment`, fileData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            }
          }
        }
      }
      
      toast.success('Quote submitted successfully!');
      setSubmitted(true);
      setSubmitting(false);
      
      // Refresh ranking data after submission
      fetchRankingData();
    } catch (error) {
      console.error('Error submitting quote:', error);
      setSubmitting(false);  // Re-enable button on error
      
      // Handle specific error cases with user-friendly messages
      const errorDetail = error.response?.data?.detail || '';
      
      if (errorDetail.includes('already submitted') || error.response?.status === 409) {
        toast.error('You have already submitted a quotation for this RFQ. You can submit a revised quote once you receive a re-quote notification email.');
      } else if (errorDetail.includes('closed')) {
        toast.error('This RFQ has been closed and is no longer accepting quotations.');
      } else if (errorDetail.includes('expired')) {
        toast.error('This RFQ link has expired. Please contact the buyer for a new link if needed.');
      } else {
        toast.error(errorDetail || 'Failed to submit quote. Please try again.');
      }
    }
  };

  const addCustomField = (type) => {
    setCustomFields([...customFields, {
      id: `field_${Date.now()}`,
      type,
      label: '',
      value: '',
      options: type === 'dropdown' ? [] : undefined,
    }]);
  };

  const updateCustomField = (id, updates) => {
    setCustomFields(customFields.map(field => 
      field.id === id ? { ...field, ...updates } : field
    ));
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(field => field.id !== id));
  };

  // Item 1 attachment handling (general attachments)
  const handleAttachmentUpload = (files) => {
    if (!files || files.length === 0) return;
    
    const newAttachments = Array.from(files).map(file => ({
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      filename: file.name,
      size: file.size,
      file: file,
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  // Item-wise attachment handling
  const handleItemAttachmentUpload = async (itemId, files) => {
    if (!files || files.length === 0) return;
    
    const newAttachments = Array.from(files).map(file => ({
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      filename: file.name,
      size: file.size,
      file: file,
    }));
    
    setItemQuotes(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, attachments: [...(item.attachments || []), ...newAttachments] }
        : item
    ));
  };

  const removeItemAttachment = (itemId, attachmentIndex) => {
    setItemQuotes(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, attachments: item.attachments.filter((_, idx) => idx !== attachmentIndex) }
        : item
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="quote-submission-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!verificationData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="invalid-token">
        <div className="bg-white p-8 rounded-lg border border-red-200 max-w-md">
          <h2 className="text-xl font-semibold text-red-900 mb-2">Invalid or Expired Link</h2>
          <p className="text-sm text-red-700">This quote submission link is no longer valid.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="quote-submitted">
        <div className="bg-white p-8 rounded-lg border border-green-200 max-w-lg text-center">
          <CheckCircle className="text-green-600 mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Quote Submitted Successfully!</h2>
          <p className="text-sm text-slate-600 mb-4">
            Thank you for submitting your quotation. The procurement team will review it shortly.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg mb-4 text-left">
            <p className="text-sm text-blue-900 font-medium mb-2">What happens next?</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li>Your quotation is now under review</li>
              <li>If you are not the lowest bidder, you may receive a re-quote notification</li>
              <li>You can revise your quote when you receive the re-quote email</li>
              <li>The buyer will award the order once the evaluation is complete</li>
            </ul>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(`${PRODUCTION_API}/quotes/${token}/download-pdf`, '_blank')}
            className="mt-2"
            data-testid="download-submitted-quote-pdf"
          >
            <Download size={16} className="mr-2" />
            Download Your Quote as PDF
          </Button>
        </div>
      </div>
    );
  }

  const { rfq, supplier, has_submitted } = verificationData;

  // Add safety check for rfq
  if (!rfq) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="invalid-token">
        <div className="bg-white p-8 rounded-lg border border-red-200 max-w-md">
          <h2 className="text-xl font-semibold text-red-900 mb-2">RFQ Not Found</h2>
          <p className="text-sm text-red-700">The requested RFQ could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Function to download quote PDF
  const handleDownloadQuotePDF = () => {
    window.open(`${PRODUCTION_API}/quotes/${token}/download-pdf`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="quote-submission-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-8 border-b border-slate-200">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" data-testid="quote-submission-title">
              Submit Quotation
            </h1>
            <p className="text-sm text-slate-500">RFQ: {rfq.rfq_number}</p>
            {has_submitted && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="text-blue-600" size={20} />
                  <p className="text-sm text-blue-900 font-semibold">Quotation Already Submitted</p>
                </div>
                <p className="text-sm text-blue-800 mb-3">
                  You have already submitted your quotation for this RFQ. If you wish to revise your quote, 
                  you can update it below. You will also receive an email notification if you are eligible for a re-quote opportunity.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadQuotePDF}
                  className="text-blue-700 border-blue-300 hover:bg-blue-100"
                  data-testid="download-existing-quote-pdf"
                >
                  <Download size={14} className="mr-1" />
                  Download Your Submitted Quote as PDF
                </Button>
              </div>
            )}
            
            {/* Current Ranking Display */}
            {rankingData && rankingData.has_quote && rankingData.your_rankings && Object.keys(rankingData.your_rankings).length > 0 && (
              <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg" data-testid="ranking-display">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="text-amber-600" size={20} />
                  <h3 className="text-lg font-semibold text-amber-900">Your Current Ranking</h3>
                  {rankingData.version > 1 && (
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                      Version {rankingData.version}
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  {Object.entries(rankingData.your_rankings).map(([itemNum, ranking]) => (
                    <div key={itemNum} className={`p-3 rounded-lg ${ranking.is_l1 ? 'bg-green-100 border border-green-300' : 'bg-white border border-amber-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            Item {itemNum}: {ranking.item_name}
                          </p>
                          {ranking.description && (
                            <p className="text-xs text-slate-500">{ranking.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                            ranking.is_l1 
                              ? 'bg-green-500 text-white' 
                              : 'bg-amber-500 text-white'
                          }`}>
                            {ranking.rank_label}
                          </span>
                          {!ranking.is_l1 && ranking.difference_from_l1 > 0 && (
                            <p className="text-xs text-red-600 mt-1 font-medium">
                              ₹{ranking.difference_from_l1.toLocaleString()} higher than L1
                            </p>
                          )}
                        </div>
                      </div>
                      {ranking.is_l1 && (
                        <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                          <CheckCircle size={12} />
                          You are the lowest bidder for this item!
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {rankingData.requote_enabled && (
                  <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="text-amber-700" size={16} />
                      <p className="text-sm text-amber-800 font-medium">
                        You can submit a revised quote to improve your ranking!
                      </p>
                    </div>
                  </div>
                )}
                
                {rankingData.version_history && rankingData.version_history.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-slate-600 cursor-pointer flex items-center gap-1 hover:text-slate-800">
                      <History size={12} />
                      View quote history ({rankingData.version_history.length} previous version{rankingData.version_history.length > 1 ? 's' : ''})
                    </summary>
                    <div className="mt-2 space-y-2">
                      {rankingData.version_history.map((version, idx) => (
                        <div key={idx} className="p-2 bg-slate-100 rounded text-xs">
                          <span className="font-medium">Version {version.version}</span>
                          <span className="text-slate-500 ml-2">
                            Rate: ₹{version.rate} | Submitted: {new Date(version.submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* ITEM 1 - Primary Item Section */}
          <div className="p-6 border-b-4 border-blue-300 bg-blue-50">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">📦 ITEM 1 (Primary): {rfq.item_name}</h2>
            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Quantity</p>
                  <p className="text-sm font-mono font-semibold">{rfq.quantity} {rfq.unit}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 uppercase">Description</p>
                  <p className="text-sm">{rfq.description}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Payment Term</p>
                  <p className="text-sm">{rfq.payment_term || 'Not specified'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 uppercase">Transport</p>
                  <p className="text-sm">{rfq.transport || 'Not specified'}</p>
                </div>
              </div>
              
              {/* Item 1 Attachments (Informational) */}
              {rfq.attachments && rfq.attachments.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 uppercase mb-2">📎 Reference Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {rfq.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/rfqs/${rfq.id}/download/${att.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        {att.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Item 1 Mandatory Requirements */}
            {rfqFieldResponses.length > 0 && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                <p className="text-sm font-medium text-amber-800 mb-3">⚠️ Mandatory Requirements for Item 1</p>
                <div className="space-y-3">
                  {rfqFieldResponses.map((field, index) => (
                    <div key={field.field_id} className="bg-white p-3 rounded border border-amber-100">
                      <Label className="text-amber-900 text-sm font-medium">
                        {index + 1}. {field.label} *
                      </Label>
                      {field.type === 'dropdown' && field.options && field.options.length > 0 ? (
                        <select
                          value={field.value}
                          onChange={(e) => updateRfqFieldResponse(field.field_id, e.target.value)}
                          className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md"
                          required
                        >
                          <option value="">-- Select --</option>
                          {field.options.map((opt, idx) => (
                            <option key={idx} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          value={field.value}
                          onChange={(e) => updateRfqFieldResponse(field.field_id, e.target.value)}
                          className="mt-1 text-sm"
                          placeholder="Your response..."
                          required
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Item 1 Quote */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <div className="flex justify-between items-start mb-3">
                <p className="text-sm font-medium text-blue-800">Your Quote for Item 1</p>
                
                {/* Item Not Available Checkbox */}
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <Checkbox
                    id="item1-not-available"
                    checked={formData.item_not_available}
                    onCheckedChange={(checked) => setFormData({ ...formData, item_not_available: checked })}
                    data-testid="item1-not-available-checkbox"
                  />
                  <Label htmlFor="item1-not-available" className="text-xs text-orange-800 font-medium cursor-pointer flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Item Not Available
                  </Label>
                </div>
              </div>
              
              {formData.item_not_available ? (
                <div className="p-4 bg-orange-100 border border-orange-300 rounded-lg text-center">
                  <AlertTriangle className="text-orange-600 mx-auto mb-2" size={32} />
                  <p className="text-sm text-orange-800 font-medium">This item will be marked as "Not Available"</p>
                  <p className="text-xs text-orange-600 mt-1">The buyer will see that you do not have this item in stock.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Rate per Unit *</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        data-testid="quote-rate-input"
                        value={formData.rate}
                        onChange={(e) => {
                          // Only allow numbers and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setFormData({ ...formData, rate: value });
                        }}
                        required
                        placeholder="Enter rate > 0"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Rate must be greater than zero</p>
                    </div>
                    <div>
                      <Label className="text-xs">MOQ (Minimum Order Quantity) *</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        data-testid="quote-moq-input"
                        value={formData.moq}
                        onChange={(e) => {
                          // Only allow numbers and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setFormData({ ...formData, moq: value });
                        }}
                        required
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Delivery Time *</Label>
                      <Input
                        data-testid="quote-delivery-input"
                        value={formData.delivery_time}
                        onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                        required
                        placeholder="e.g., 7-10 days"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Item 1 - Supplier Attachments */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <Label className="text-xs text-blue-700">📎 Attach Files for Item 1 (Optional)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleAttachmentUpload(e.target.files)}
                    className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                  />
                </div>
                {attachments && attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {attachments.map((att, attIdx) => (
                      <div key={attIdx} className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        <FileText size={12} />
                        <span>{att.filename}</span>
                        <button
                          type="button"
                          onClick={() => setAttachments(attachments.filter((_, idx) => idx !== attIdx))}
                          className="ml-1 text-blue-800 hover:text-red-600"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* ADDITIONAL ITEMS (Item 2, 3, etc.) */}
          {itemQuotes.map((item, index) => {
            // Get the original item data from rfq.items to access attachments
            const originalItem = rfq.items?.find(i => i.id === item.item_id) || {};
            
            return (
            <div key={item.item_id} className="p-6 border-b-4 border-green-300 bg-green-50">
              <h2 className="text-xl font-semibold text-green-900 mb-4">📦 ITEM {index + 2}: {item.item_name}</h2>
              <div className="bg-white p-4 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Quantity</p>
                    <p className="text-sm font-mono font-semibold">{item.quantity} {item.unit}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase">Description</p>
                    <p className="text-sm">{item.description || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Payment Term</p>
                    <p className="text-sm">{originalItem.payment_term || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase">Transport</p>
                    <p className="text-sm">{originalItem.transport || 'Not specified'}</p>
                  </div>
                </div>
                
                {/* Item Reference Attachments (Informational) */}
                {originalItem.attachments && originalItem.attachments.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-slate-500 uppercase mb-2">📎 Reference Attachments</p>
                    <div className="flex flex-wrap gap-2">
                      {originalItem.attachments.map((att, idx) => (
                        <a
                          key={idx}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/rfqs/${rfq.id}/download/${att.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          {att.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Item Mandatory Requirements */}
              {item.custom_field_responses && item.custom_field_responses.length > 0 && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                  <p className="text-sm font-medium text-amber-800 mb-3">⚠️ Mandatory Requirements for Item {index + 2}</p>
                  <div className="space-y-3">
                    {item.custom_field_responses.map((field, fieldIdx) => (
                      <div key={field.field_id} className="bg-white p-3 rounded border border-amber-100">
                        <Label className="text-amber-900 text-sm font-medium">
                          {fieldIdx + 1}. {field.label} *
                        </Label>
                        {field.type === 'dropdown' && field.options && field.options.length > 0 ? (
                          <select
                            value={field.value}
                            onChange={(e) => updateItemCustomFieldResponse(item.item_id, field.field_id, e.target.value)}
                            className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md"
                            required
                          >
                            <option value="">-- Select --</option>
                            {field.options.map((opt, idx) => (
                              <option key={idx} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={field.value}
                            onChange={(e) => updateItemCustomFieldResponse(item.item_id, field.field_id, e.target.value)}
                            className="mt-1 text-sm"
                            placeholder="Your response..."
                            required
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Item Quote */}
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <div className="flex justify-between items-start mb-3">
                  <p className="text-sm font-medium text-green-800">Your Quote for Item {index + 2}</p>
                  
                  {/* Item Not Available Checkbox */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                    <Checkbox
                      id={`item${index + 2}-not-available`}
                      checked={item.item_not_available || false}
                      onCheckedChange={(checked) => updateItemQuote(item.item_id, { item_not_available: checked })}
                      data-testid={`item${index + 2}-not-available-checkbox`}
                    />
                    <Label htmlFor={`item${index + 2}-not-available`} className="text-xs text-orange-800 font-medium cursor-pointer flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Item Not Available
                    </Label>
                  </div>
                </div>
                
                {item.item_not_available ? (
                  <div className="p-4 bg-orange-100 border border-orange-300 rounded-lg text-center">
                    <AlertTriangle className="text-orange-600 mx-auto mb-2" size={32} />
                    <p className="text-sm text-orange-800 font-medium">This item will be marked as "Not Available"</p>
                    <p className="text-xs text-orange-600 mt-1">The buyer will see that you do not have this item in stock.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Rate per Unit *</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.rate}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            updateItemQuote(item.item_id, { rate: value });
                          }}
                          required
                          placeholder="Enter rate > 0"
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Rate must be greater than zero</p>
                      </div>
                      <div>
                        <Label className="text-xs">MOQ (Minimum Order Quantity)</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.moq}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            updateItemQuote(item.item_id, { moq: value });
                          }}
                          placeholder="Optional"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Delivery Time</Label>
                        <Input
                          value={item.delivery_time}
                          onChange={(e) => updateItemQuote(item.item_id, { delivery_time: e.target.value })}
                          placeholder="e.g., 7-10 days"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {/* Item-wise Supplier Attachments */}
                <div className="mt-4 pt-4 border-t border-green-200">
                  <Label className="text-xs text-green-700">📎 Attach Files for Item {index + 2} (Optional)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleItemAttachmentUpload(item.item_id, e.target.files)}
                      className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                    />
                  </div>
                  {item.attachments && item.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {item.attachments.map((att, attIdx) => (
                        <div key={attIdx} className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                          <FileText size={12} />
                          <span>{att.filename}</span>
                          <button
                            type="button"
                            onClick={() => removeItemAttachment(item.item_id, attIdx)}
                            className="ml-1 text-green-800 hover:text-red-600"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}

          {/* GENERAL SECTION - Terms & Attachments */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">General Terms & Attachments</h2>
              
              <div className="mb-4">
                <Label htmlFor="terms">Terms & Conditions (applies to all items)</Label>
                <Textarea
                  id="terms"
                  rows={3}
                  data-testid="quote-terms-input"
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  placeholder="Enter payment terms, warranty details, delivery conditions, etc."
                  className="mt-1"
                />
              </div>

              {/* Custom Fields Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <Label>Additional Information (Optional)</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => addCustomField('text')}>
                      + Add Field
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => addCustomField('dropdown')}>
                      + Dropdown
                    </Button>
                  </div>
                </div>
                
                {customFields.map((field, index) => (
                  <div key={field.id} className="p-3 border rounded-lg mb-2 bg-slate-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        Field #{index + 1}
                      </span>
                      <button type="button" onClick={() => removeCustomField(field.id)} className="text-red-600 text-sm">Remove</button>
                    </div>
                    
                    <Input
                      placeholder="Label (e.g., Warranty Period, Certification)"
                      value={field.label}
                      onChange={(e) => updateCustomField(field.id, { label: e.target.value })}
                      className="mb-2"
                    />
                    
                    {field.type === 'text' && (
                      <Input
                        placeholder="Value"
                        value={field.value}
                        onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
                      />
                    )}
                    
                    {field.type === 'dropdown' && (
                      <>
                        <Input
                          placeholder="Options (comma-separated)"
                          value={field.options ? field.options.join(', ') : ''}
                          onChange={(e) => updateCustomField(field.id, { options: e.target.value.split(',').map(o => o.trim()) })}
                          className="mb-2"
                        />
                        <select
                          value={field.value}
                          onChange={(e) => updateCustomField(field.id, { value: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-md"
                        >
                          <option value="">Select value</option>
                          {field.options && field.options.map((opt, idx) => (
                            <option key={idx} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                ))}
                
                {customFields.length === 0 && (
                  <p className="text-xs text-slate-500 text-center py-2">
                    Add any additional information relevant to your quotation
                  </p>
                )}
              </div>

              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  data-testid="submit-quote-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting...
                    </>
                  ) : (
                    has_submitted ? 'Update Quotation' : 'Submit Quotation'
                  )}
                </Button>
                {submitting && (
                  <p className="text-xs text-center text-amber-600 mt-2">
                    Please wait while your quotation is being submitted. Do not close this page.
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-slate-500">
        <p>Supplier: {supplier?.name || 'Unknown'}</p>
      </div>
    </div>
  );
};

export default QuoteSubmission;